import React, { useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Card, User } from '../../service/api/types';
import { useManagerCards } from '../../store/managerCards';
import MultiSelectDropdown from '../MultiSelectDropdown';

interface CardDeliveryModalProps {
  show: boolean;
  onHide: () => void;
  card: Card | null;
  managedUsers: User[];
}

export const CardDeliveryModal: React.FC<CardDeliveryModalProps> = ({
  show,
  onHide,
  card,
  managedUsers,
}) => {
  const { t } = useTranslation();
  const { deliverCard } = useManagerCards();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isDelivering, setIsDelivering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDeliver = async () => {
    if (!card || selectedUserIds.length === 0) return;

    setIsDelivering(true);
    setError(null);

    try {
      await deliverCard(card._id, selectedUserIds);
      onHide();
      setSelectedUserIds([]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('cards.failedToDeliverCard'),
      );
    } finally {
      setIsDelivering(false);
    }
  };

  const getActionsSummary = () => {
    if (!card) return '';

    return card.actions
      .map(action => {
        if (action.type === 'sticker') {
          return action.amount > 0
            ? t('cards.actions.giveStickers', {
                count: Math.abs(action.amount),
              })
            : t('cards.actions.takeStickers', {
                count: Math.abs(action.amount),
              });
        } else if (action.type === 'item') {
          const itemName = action.item?.title || t('common.item');
          return t('cards.actions.giveItems', {
            count: action.amount,
            name: itemName,
          });
        } else {
          // effect action
          const label =
            action.effectType === 'set_price'
              ? t('effects.setPrice')
              : action.effectType === 'adjust_all_prices_percent'
                ? t('effects.adjustAllPrices')
                : t('effects.adjustItemPrice');
          return `⚡ ${label} (${action.value > 0 ? '+' : ''}${action.value}${action.effectType !== 'set_price' ? '%' : ''})`;
        }
      })
      .join(', ');
  };

  const userOptions = managedUsers.map(user => ({
    value: user._id,
    label: `${user.given_name} ${user.family_name}`,
    sublabel: user.email,
  }));

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('cards.deliverCard')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {card && (
          <>
            <div className="mb-3">
              <h6 className="fw-bold d-flex align-items-center">
                {card.icon && <span className="me-2">{card.icon}</span>}
                {card.title}
              </h6>
              <p className="text-muted mb-2">{card.description}</p>
              <div className="small text-muted">
                <strong>{t('cards.actionsLabel')}:</strong>{' '}
                {getActionsSummary()}
              </div>
            </div>

            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <div className="mb-3">
              <label className="form-label">
                {t('cards.selectUsersToDeliver')} *
              </label>
              <MultiSelectDropdown
                options={userOptions}
                value={selectedUserIds}
                onChange={setSelectedUserIds}
                placeholder={t('cards.chooseUsers')}
              />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isDelivering}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleDeliver}
          disabled={isDelivering || selectedUserIds.length === 0}
        >
          {isDelivering ? (
            <>
              <Spinner size="sm" className="me-2" />
              {t('cards.delivering')}
            </>
          ) : selectedUserIds.length > 0 ? (
            t('cards.deliverToUsers', { count: selectedUserIds.length })
          ) : (
            t('cards.deliver')
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
